import {
  EBeneficiaryType,
  ECreateLoanStatus,
  ECreditApplicationStatus,
  EFinancingProgram,
  ELoanPurpose,
  EOriginSystemId,
  EProgressionStatus,
} from '@if/core/types'
import { copyMessage, formatCurrency, formatDate } from '@if/core/utils'
import { EditNote } from '@mui/icons-material'
import BlockIcon from '@mui/icons-material/Block'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import InfoIcon from '@mui/icons-material/Info'
import LanguageIcon from '@mui/icons-material/Language'
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { Badge, Box, Button, ButtonGroup, Grid, IconButton, Link, Stack, SvgIcon, Tooltip } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import LoopIcon from '@src/assets/loop.svg?react'
import {
  useChangeRush,
  useDownloadFCFDocument,
  useGetCreateLoanStatus,
  useLockCreditApplication,
  usePayOut,
  useSendHighEstimateFundingConfirmation,
  useUnlockCreditApplication,
} from '@src/data/api/credit-application/credit-application-api'
import { useCreateLoan, useCreateLoanV2 } from '@src/data/api/credit-loan/credit-application-loan'
import { useGetTaskByCreditApplicationId } from '@src/data/api/credit-task/credit-task-api'
import { useAddFCFNote } from '@src/data/api/document/document-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useFeatureFlags, useSetBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { useGetApplyOnlineLink } from '@src/data/selectors/credit-selectors'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LanguageProvinceIndicator from '../../../components/LanguageProvinceIndicator'
import { Constants, CreditApplication, CreditApplicationMessage, User, useDevTools } from '../../../data/types'
import { Merchant } from '../../../data/types/Merchant'
import CopyLoanAsDraftBtn from './CopyLoanAsDraftBtn'
import EmailAddress from './EmailAddress'
import FCFNoteDialog from './FCFNoteDialog'
import FollowUpAgentBadge from './FollowUpAgentBadge'
import PhoneNumber from './PhoneNumber'
import UnregisteredMerchantNameInput from './UnregisteredMerchantNameInput'

interface Props {
  creditApplication: CreditApplication
  creditApplicationMessage: CreditApplicationMessage[]
  merchant: Merchant
  onStatusChange: () => void
  onMerchantChanged: () => void
  creditApplicationCanBeLocked: boolean
  loanId: number | undefined | null
  user: User | null
  applicationStatus: EProgressionStatus
  editDisabled: boolean
  finalDecisionIsPending: boolean
  allTasksRequiredForPayoutCompleted: boolean
  allDocumentsRequiredForPayOutCompleted: boolean
  hasDocumentsPendingApproval: boolean
  ishighEstimate: boolean
  hasConfirmedHighEstimateFunding: boolean
  onBanApplicant: () => void
  incomeConfirmed: boolean
}

const ApplicationResume = ({
  creditApplication,
  creditApplicationMessage,
  merchant,
  onStatusChange,
  onMerchantChanged,
  creditApplicationCanBeLocked,
  loanId,
  user,
  applicationStatus,
  editDisabled,
  finalDecisionIsPending,
  allTasksRequiredForPayoutCompleted,
  allDocumentsRequiredForPayOutCompleted,
  hasDocumentsPendingApproval,
  ishighEstimate,
  hasConfirmedHighEstimateFunding,
  onBanApplicant,
  incomeConfirmed,
}: Props) => {
  const { t } = useTranslation()
  const setBusinessErrors = useSetBusinessErrors()
  const featureFlags = useFeatureFlags()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
  const renderCancelButtonText = useMemo(() => {
    if (creditApplication.status !== ECreditApplicationStatus.Cancelled) return t('common.cancel')
    return t('common.reactivate')
  }, [creditApplication.status, t])

  const financingProgramConfig = Constants.financingProgramConfigs[creditApplication.financingProgramId]
  const applyOnlineLink = useGetApplyOnlineLink()
  const webAppLink = applyOnlineLink
  const activateDownloadFCFButton = featureFlags.activateDownloadFCFButton

  const containsAtSymbol = (str: string): boolean => {
    return str.includes('@')
  }

  const isReadyToCreateLoan = Boolean(
    (creditApplication.financingProgramId === EFinancingProgram.AutoRsla ||
      creditApplication.financingProgramId === EFinancingProgram.CreditRsla ||
      (creditApplication.financingProgramId !== EFinancingProgram.Auto && creditApplicationCanBeLocked) ||
      creditApplication.editLocked) &&
      creditApplication.loanId == null &&
      creditApplication.cvtNumber != null &&
      creditApplication.paymentBankAccount &&
      !finalDecisionIsPending &&
      allTasksRequiredForPayoutCompleted &&
      allDocumentsRequiredForPayOutCompleted &&
      !hasDocumentsPendingApproval,
  )

  const canCreateLoan = user?.rights.canCreateLoan === true && isReadyToCreateLoan

  const [changeRushHook, isLoadingRush] = useChangeRush()
  const [lockCreditApplicationHook] = useLockCreditApplication()
  const [unlockCreditApplicationHook] = useUnlockCreditApplication()
  const [payOutHook] = usePayOut()
  const [downloadFCFDocument] = useDownloadFCFDocument()

  const { refetch: refetchTasks } = useGetTaskByCreditApplicationId(
    creditApplication.financingProgramId,
    creditApplication.id,
  )

  const handleLockingCreditApplication = useCallback(async () => {
    if (creditApplication?.id) {
      try {
        if (!creditApplication.editLocked) {
          await lockCreditApplicationHook({
            creditApplicationId: creditApplication.id,
            financingProgramId: creditApplication.financingProgramId,
          })
        } else {
          await unlockCreditApplicationHook({
            creditApplicationId: creditApplication.id,
            financingProgramId: creditApplication.financingProgramId,
          })
        }
      } catch (error) {
        reportErrorToConsole(error)
      }
    }
  }, [
    creditApplication?.id,
    creditApplication?.editLocked,
    creditApplication.financingProgramId,
    lockCreditApplicationHook,
    unlockCreditApplicationHook,
  ])

  const onHandlePayOut = useCallback(async () => {
    if (creditApplication?.id) {
      try {
        await payOutHook({
          creditApplicationId: creditApplication.id,
          financingProgramId: creditApplication.financingProgramId,
        })
      } catch (error) {
        reportErrorToConsole(error)
      }
    }
  }, [creditApplication?.id, creditApplication?.financingProgramId, payOutHook])

  const onHandleDownloadFCF = useCallback(async () => {
    if (creditApplication?.id) {
      try {
        await downloadFCFDocument(creditApplication.id, creditApplication.financingProgramId)
      } catch (error) {
        reportErrorToConsole(error)
      }
    }
  }, [creditApplication, downloadFCFDocument])

  const { mutate: addFCFNoteMutation, isPending: isAddingFCFNote } = useAddFCFNote(
    creditApplication.financingProgramId,
    creditApplication.id,
  )

  const [fcfNoteDialogOpen, setFcfNoteDialogOpen] = useState(false)

  const handleAddFCFNote = useCallback(
    (noteText: string) => {
      addFCFNoteMutation(
        {
          note: noteText,
        },
        {
          onSuccess: () => {
            setFcfNoteDialogOpen(false)
          },
          onError: (error) => {
            reportErrorToConsole(error)
          },
        },
      )
    },
    [addFCFNoteMutation],
  )

  const [shouldPollCreateLoanStatus, setShouldPollCreateLoanStatus] = useState(false)

  const [pollCreateLoanStatus, isPollingCreateLoanStatus, refetch] = useGetCreateLoanStatus(
    creditApplication.financingProgramId,
    creditApplication.id,
    shouldPollCreateLoanStatus,
  )
  const handleHighEstimateFundingConfirmation = async () => {
    await sendHighEstimateFundingConfirmation()
    await refetchTasks()
  }
  const [createLoan, isCreateLoanPending] = useCreateLoan()
  const [createLoanV2, isCreateLoanV2Pending] = useCreateLoanV2()
  const [sendHighEstimateFundingConfirmation, isSendingHighEstimateFundingConfirmation] =
    useSendHighEstimateFundingConfirmation(creditApplication.financingProgramId, creditApplication.id)

  useEffect(() => {
    const handlePollStatus = async () => {
      if (shouldPollCreateLoanStatus && !isPollingCreateLoanStatus) {
        const status = pollCreateLoanStatus?.runtimeStatus

        if (status === ECreateLoanStatus.NotCreated || status === ECreateLoanStatus.Failed) {
          setShouldPollCreateLoanStatus(false)
          if (status === ECreateLoanStatus.Failed) {
            refetchTasks().catch(reportErrorToConsole)
          }
        }

        if (status === ECreateLoanStatus.Completed) {
          setShouldPollCreateLoanStatus(false)
          await invalidateFullCreditApplication(creditApplication.id, creditApplication.financingProgramId)
        }
      }
    }
    handlePollStatus().catch(reportErrorToConsole)
  }, [
    shouldPollCreateLoanStatus,
    isPollingCreateLoanStatus,
    pollCreateLoanStatus?.runtimeStatus,
    creditApplication.id,
    creditApplication.financingProgramId,
    refetchTasks,
    invalidateFullCreditApplication,
  ])
  const handleCreateLoan = useCallback(async () => {
    if (!creditApplication?.loanId && !isCreateLoanPending) {
      try {
        await createLoan({
          financingProgramId: creditApplication.financingProgramId,
          id: creditApplication.id,
        })
        setShouldPollCreateLoanStatus(true)
        await refetch()
      } catch (handleLoanError) {
        reportErrorToConsole(handleLoanError)
        if (!financingProgramConfig.useCreateAlisLoan) {
          setBusinessErrors([{ message: 'editCreditApplication.errors.createLoan' }])
        }
        setShouldPollCreateLoanStatus(true)
        try {
          await refetch()
        } catch (refetchError) {
          reportErrorToConsole(refetchError)
        }
      }
    }
  }, [
    creditApplication?.loanId,
    creditApplication?.financingProgramId,
    creditApplication?.id,
    isCreateLoanPending,
    setBusinessErrors,
    refetch,
    financingProgramConfig.useCreateAlisLoan,
    createLoan,
  ])

  const handleCreateLoanV2 = useCallback(async () => {
    if ((canCreateLoan || creditApplication?.loanId !== null) && !isCreateLoanV2Pending) {
      try {
        await createLoanV2({
          financingProgramId: creditApplication.financingProgramId,
          id: creditApplication.id,
        })
      } catch (handleLoanError) {
        reportErrorToConsole(handleLoanError)
        if (!financingProgramConfig.useCreateAlisLoan) {
          setBusinessErrors([{ message: 'editCreditApplication.errors.createLoan' }])
        }
      }
    }
  }, [
    canCreateLoan,
    creditApplication?.loanId,
    creditApplication.financingProgramId,
    creditApplication.id,
    isCreateLoanV2Pending,
    financingProgramConfig.useCreateAlisLoan,
    setBusinessErrors,
    createLoanV2,
  ])
  const handleViewLoan = useCallback(() => {
    window.open(
      `${import.meta.env.VITE_MONOLITH_API_URL}/admin/ppa/customer.aspx?id=${creditApplication?.loanId}`,
      '_blank',
    )
  }, [creditApplication?.loanId])

  const applicationIsDraft = creditApplication.status === ECreditApplicationStatus.Draft

  function getBeneficiaryName() {
    const {
      beneficiaryTypeId,
      applicant,
      coApplicant,
      otherBeneficiaryFirstName,
      otherBeneficiaryLastName,
      loanPurposeId,
    } = creditApplication

    switch (beneficiaryTypeId) {
      case EBeneficiaryType.Applicant:
        return `${applicant?.firstName} ${applicant?.lastName}`
      case EBeneficiaryType.Coapplicant:
        return `${coApplicant?.firstName} ${coApplicant?.lastName}`
      case EBeneficiaryType.Other:
        if (loanPurposeId === ELoanPurpose.Veterinary) {
          return `${otherBeneficiaryLastName}`
        }
        if (otherBeneficiaryFirstName === null || otherBeneficiaryLastName === null) {
          return ''
        }
        return `${otherBeneficiaryFirstName} ${otherBeneficiaryLastName}`
      default:
        return ''
    }
  }
  const isRush = creditApplication?.isRush === true

  const handleToggleRush = async () => {
    if (creditApplication && creditApplication.versionTag) {
      try {
        await changeRushHook({
          financingProgramId: creditApplication.financingProgramId,
          creditApplicationId: creditApplication.id,
          isRush: !isRush,
          versionTag: creditApplication.versionTag,
        })
      } catch (error) {
        reportErrorToConsole(error)
      }
    }
  }
  const isMerchandStateIso =
    creditApplication.financingProgramId === EFinancingProgram.Auto ||
    creditApplication.originSystemId === EOriginSystemId.MerchantDashboard

  return (
    <Grid container spacing={2} sx={{ mb: 2 }} size={12}>
      <Grid
        size={{
          xs: 12,
          md: 12,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <span>
            <Typography variant="h6" component="div">
              {t('common.application')} : #{creditApplication.referenceNumber}
              {creditApplication?.parentCreditApplicationId && (
                <>
                  {' ('}
                  {t('credit.refinancing')}{' '}
                  <Link
                    rel="noopener noreferrer"
                    target="_blank"
                    href={`/Applications/${creditApplication.financingProgramId}/${creditApplication.parentCreditApplicationId}/view`}
                  >
                    {creditApplication.parentReferenceNumber}
                  </Link>
                  {' )'}
                </>
              )}
            </Typography>

            {financingProgramConfig.useCreateAlisLoan && creditApplication.loanId && (
              <Typography variant="h6" component="div">
                {t('common.loan')} : #{creditApplication.loanId}
              </Typography>
            )}
            {creditApplication.financingProgramId === EFinancingProgram.Auto && (
              <Typography variant="h6" component="div" gutterBottom>
                #DT: #TODO
              </Typography>
            )}
          </span>
          {creditApplication.financingProgramId === EFinancingProgram.Auto && (
            <IconButton disabled={applicationIsDraft} aria-label="Ice-center" color="primary">
              <LanguageIcon fontSize="large" />
            </IconButton>
          )}
          <Typography variant="body1" component="div" sx={{ border: '1px solid', borderRadius: 2, p: 1 }}>
            {t('editCreditApplication.beneficiary.beneficiary')}: {getBeneficiaryName()}
          </Typography>
          {creditApplication.isInvisalign && (
            <Typography variant="body1" component="div" sx={{ border: '1px solid', borderRadius: 2, p: 1 }}>
              {t('editCreditApplication.invisalign.indicator')}
            </Typography>
          )}
          <Typography variant="body1" component="div" sx={{ border: '1px solid', borderRadius: 2, p: 1 }}>
            {t('common.program')} : {t(`EFinancingProgram.${creditApplication.financingProgramId}`, { ns: 'enum' })}
          </Typography>
          {creditApplication.financingProgramId === EFinancingProgram.Auto && (
            <Badge badgeContent={creditApplicationMessage.length} color="success">
              <Button
                variant="outlined"
                size="large"
                onClick={() =>
                  openInNewTab(`/Applications/${creditApplication.financingProgramId}/${creditApplication.id}/messages`)
                }
              >
                {t('message.message', '', { count: creditApplicationMessage.length })}
              </Button>
            </Badge>
          )}
          {creditApplication.financingProgramId === EFinancingProgram.Auto && (
            <Badge color="success">
              <Button
                variant="outlined"
                size="large"
                disabled={
                  !(creditApplicationCanBeLocked && user?.rights.canEditCreditApp && user?.isInternal && !editDisabled)
                }
                onClick={handleLockingCreditApplication}
              >
                {creditApplication.editLocked ? t('lockCredit.unlockCredit') : t('lockCredit.lockCredit')}
              </Button>
            </Badge>
          )}

          <div>
            {isMerchandStateIso && <StorefrontIcon fontSize="large" color="primary" />}

            <LanguageProvinceIndicator
              correspondanceLanguageId={creditApplication.correspondanceLanguageId}
              applicantStateIso={
                isMerchandStateIso ? merchant.province : (creditApplication.applicant.currentAddress.stateIso ?? null)
              }
            />
          </div>
        </Stack>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 8,
        }}
      >
        <Box sx={{ border: '1px solid grey', borderRadius: 2, p: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <span>
              <Typography>
                {t('common.origin')} : {t(`EOriginSystemId.${creditApplication.originSystemId}`, { ns: 'enum' })}
              </Typography>
              {creditApplication.financingProgramId === EFinancingProgram.Personal && (
                <Typography variant="body1" component="div">
                  {t('editCreditApplication.loanPurpose')} :{' '}
                  {t(`ELoanPurpose.${creditApplication.loanPurposeId}`, { ns: 'enum' })}
                </Typography>
              )}
              <Typography variant="body1" component="div">
                {t('common.status')} :{' '}
                {applicationIsDraft
                  ? t('ECreditApplicationStatus.draft', { ns: 'enum' })
                  : t(`EProgressionStatusAbbr.${applicationStatus ?? 'unknown'}`, { ns: 'enum' })}
              </Typography>
              <Typography variant="body1" component="div" gutterBottom>
                {t('common.createdOn')} : {formatDate(creditApplication.createdOn)}
              </Typography>
              <Typography
                variant="body1"
                component="div"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {t('common.expiresOn')} : {formatDate(creditApplication.expiresOn)}
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {t('expirationToolTipe.title')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('expirationToolTipe.expiresOn')}{' '}
                        {new Date(creditApplication.expiresOn).toLocaleString('en-CA', { timeZone: 'UTC' })}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('expirationToolTipe.currentTime')} {new Date().toLocaleString('en-CA', { timeZone: 'UTC' })}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('expirationToolTipe.expireRunAt')}
                      </Typography>
                    </Box>
                  }
                  placement="right"
                  describeChild
                >
                  <IconButton size="small" sx={{ padding: 0 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
            </span>
            <span>
              {financingProgramConfig.shouldSeeAmounts && (
                <div>
                  <Typography variant="body1" component="div">
                    {t('editCreditApplication.amountRequested')} :{' '}
                    {formatCurrency(creditApplication.initialRequestedLoanAmount ?? 0)}
                  </Typography>
                  <Typography variant="body1" component="div">
                    {t('editCreditApplication.amountGranted')} :{' '}
                    {formatCurrency(creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0)}
                  </Typography>
                  <Typography variant="body1" component="div">
                    {t('editCreditApplication.maximumLoanAmount')} :{' '}
                    {formatCurrency(creditApplication?.finalCreditDecision?.maximumAvalaibleAmount ?? 0)}
                  </Typography>
                </div>
              )}
              <Typography variant="body1" component="div">
                {t('common.merchant')} : {merchant?.name}
                {featureFlags.usePersonalLoanServiceProviderName && (
                  <UnregisteredMerchantNameInput creditApplication={creditApplication} serviceProvider={creditApplication.unregisteredMerchantName ?? undefined} merchant={merchant} />
                )}
              </Typography>

              {merchant && (
                <Typography variant="body1" component="div">
                  {t('editCreditApplication.merchantReferenceCode')} : {creditApplication?.merchantReferenceCode ?? ''}
                </Typography>
              )}
              <Typography variant="body1" component="div" gutterBottom>
                {t('common.by')} : {creditApplication.createdByUserFullname}
              </Typography>
              {containsAtSymbol(creditApplication.createdByUserId) && (
                <Typography variant="body1" component="div">
                  <EmailAddress email={creditApplication.createdByUserId} />
                </Typography>
              )}
              <PhoneNumber phone={merchant?.phone} applyPhoneFormatting={false} contacts={merchant.contacts} />
            </span>
            <span>
              {featureFlags.useFollowUpAgentId && creditApplication.financingProgramId === EFinancingProgram.Personal && (
                <FollowUpAgentBadge
                  followUpAgent={creditApplication?.followUpAgent}
                  creditApplicationId={creditApplication.id}
                  financingProgramId={creditApplication.financingProgramId}
                />
              )}
              <span>
                <IconButton
                  aria-label="toggle-rush"
                  onClick={handleToggleRush}
                  disabled={!user?.rights.canManageRushTag || isLoadingRush}
                >
                  {isLoadingRush ? (
                    <CircularProgress size={24} />
                  ) : (
                    <SvgIcon fontSize="large" style={{ color: isRush ? 'red' : undefined }}>
                      <InfoIcon fontSize="small" />
                    </SvgIcon>
                  )}
                </IconButton>
              </span>
              {webAppLink && (
                <IconButton color="success" onClick={() => copyMessage(webAppLink)}>
                  <SvgIcon fontSize="large">
                    <ContentCopyIcon />
                  </SvgIcon>
                </IconButton>
              )}
              <IconButton
                aria-label="swap"
                onClick={onMerchantChanged}
                disabled={!user?.rights.canChangeMerchant || editDisabled}
              >
                <SvgIcon fontSize="large">
                  <LoopIcon />
                </SvgIcon>
              </IconButton>
            </span>
          </Stack>
        </Box>
      </Grid>
      <Grid
        container
        spacing={2}
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <Grid size="grow">
          <Stack direction="column" justifyContent="space-between" height="100%" spacing={1}>
            <div>
              {creditApplication.financingProgramId === EFinancingProgram.Auto && (
                <ButtonGroup variant="outlined" size="large" sx={{ width: '100%' }}>
                  <Button
                    fullWidth
                    size="large"
                    onClick={onHandlePayOut}
                    disabled={!user?.rights.canSetDisbursed || applicationIsDraft || editDisabled}
                  >
                    {t('common.funding')}
                  </Button>
                  {!activateDownloadFCFButton && (
                    <Button disabled={applicationIsDraft} size="small">
                      <LocalPrintshopIcon fontSize="large" />
                    </Button>
                  )}
                </ButtonGroup>
              )}
              {creditApplication.financingProgramId === EFinancingProgram.Personal && (
                <ButtonGroup variant="outlined" size="large" sx={{ width: '100%' }}>
                  {!activateDownloadFCFButton && (
                    <Button disabled={applicationIsDraft} size="small">
                      <LocalPrintshopIcon fontSize="large" />
                    </Button>
                  )}
                </ButtonGroup>
              )}
              <>
                {activateDownloadFCFButton && creditApplication.financingProgramId === EFinancingProgram.Personal && (
                  <ButtonGroup variant="outlined" size="large" sx={{ width: '100%' }}>
                    {!creditApplication.loanId && (
                      <Button
                        fullWidth
                        size="large"
                        onClick={() => {
                          setFcfNoteDialogOpen(true)
                        }}
                        disabled={isAddingFCFNote}
                        endIcon={<EditNote fontSize="large" />}
                      >
                        {isAddingFCFNote ? 'Adding note...' : t('editCreditApplication.FinancingConfirmationForm.note')}
                      </Button>
                    )}
                    {creditApplication.loanId && (
                      <Button
                        fullWidth
                        size="large"
                        onClick={onHandleDownloadFCF}
                        disabled={!incomeConfirmed}
                        endIcon={<DownloadIcon />}
                      >
                        {t('editCreditApplication.FinancingConfirmationForm.btn')}
                      </Button>
                    )}
                  </ButtonGroup>
                )}
              </>
            </div>
            <CopyLoanAsDraftBtn
              creditApplicationId={creditApplication.id}
              financingProgramId={creditApplication.financingProgramId}
            />
          </Stack>
        </Grid>

        <Grid size="grow">
          <Stack direction="column" justifyContent="space-between" alignItems="flex-center" height="100%">
            <ButtonGroup variant="outlined" size="large" sx={{ width: '100%' }}>
              {!loanId && (
                <Button
                  variant="outlined"
                  size="large"
                  disabled={!(canCreateLoan && !editDisabled && !isCreateLoanPending && !shouldPollCreateLoanStatus)}
                  onClick={handleCreateLoan}
                  sx={{ width: '100%' }}
                >
                  {t('common.createLoan')}
                  {(isCreateLoanPending || shouldPollCreateLoanStatus) && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </Button>
              )}
              {loanId && (
                <Button
                  variant="outlined"
                  size="large"
                  disabled={financingProgramConfig.useCreateAlisLoan}
                  onClick={handleViewLoan}
                  sx={{ width: '100%' }}
                >
                  {t('common.consultLoan')}
                </Button>
              )}
              {useDevTools && (
                <Button
                  variant="outlined"
                  size="large"
                  disabled={isCreateLoanV2Pending || (!canCreateLoan && !loanId)}
                  onClick={handleCreateLoanV2}
                  sx={{ width: '100%' }}
                >
                  {t('common.createLoanV2')}
                  {isCreateLoanV2Pending && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </Button>
              )}
            </ButtonGroup>

            <Button
              variant="outlined"
              size="large"
              onClick={onStatusChange}
              disabled={!(user?.rights.canEditCreditApp && !editDisabled)}
            >
              {renderCancelButtonText}
            </Button>
            {user?.rights.canBanApplicant && (
              <Button
                variant="outlined"
                size="large"
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => {
                  onBanApplicant()
                }}
                disabled={editDisabled || creditApplication.finalCreditDecision?.refusalReasonCodes?.includes('Banned')}
                fullWidth
              >
                {t('ban.banButtonLabel')}
              </Button>
            )}
          </Stack>
        </Grid>
        {ishighEstimate && (
          <Button
            fullWidth
            size="large"
            variant="outlined"
            onClick={handleHighEstimateFundingConfirmation}
            disabled={isSendingHighEstimateFundingConfirmation}
          >
            {hasConfirmedHighEstimateFunding
              ? t('common.ResendHighEstimateConfirmFunding')
              : t('common.HighEstimateConfirmFunding')}
          </Button>
        )}
      </Grid>
      <FCFNoteDialog
        open={fcfNoteDialogOpen}
        isLoading={isAddingFCFNote}
        onClose={() => setFcfNoteDialogOpen(false)}
        onSubmit={handleAddFCFNote}
        initialNote={creditApplication.fcfNote}
      />
    </Grid>
  )
}


export default ApplicationResume
